/**
 *
 * @module component/extender
 * @author Adam Pirani [adam.pirani@mlb.com]
 * @description component data extender
 *
 */

import get from 'lodash-es/get';
import helpers from './helpers';
const { i18n } = helpers;
import packageJSON from './../../../package.json';
const { version } = packageJSON;
import extenderExtend from './extender/extend';
const { extendTeams, extendLinescore, extendStatus, extendMlbButtons } = extenderExtend;
import extenderGet from './extender/get';
const { getGameTimePostseasonPush, getGameTime, getGameNote, getPlayers, getMode, getSport, getButtonOptions, getChallengingTeam } = extenderGet;
const { getGameClasses, getGameWrapperClass, getDate, getFlag, getGameTracking, getGameSponsor } = extenderGet;
const { getBroadcasts, getTeamLeaders, getVideoId, getHomeRuns, hasMiniButtonsSm, getDescription } = extenderGet;
const { getPromotion, getGameTypeDesc, getPostseasonInfo } = extenderGet;
import extenderGetStat from './extender/getStat';
const { HEADSHOT_SILHOUETTE } = extenderGetStat;
const SITE_ASSET_URI = '/node_modules/mlb-site-core/site-core';
const SITE_IMAGE_URI = 'https://prod-gameday.mlbstatic.com/responsive-gameday-assets';
const MODE_HAS_BUTTONS = {
    responsive: true,
    MIG: true,
    minisb: true
};
let flipDate;
/**
 * @typedef {object} schedule_data_type full response from schedule API
 * @typedef {object} single_game_type a single game (subset of the above)
 */


/**
 * @function hideMLBTV
 * @description removes the watch mlb.tv object
 * @param {object} gameData
 * @param {object} mlbButtons
 */
function hideMLBTV(gameData = {}, mlbButtons = {}) {
    const currentSeason = 2018;
    const gameSeasonStr = gameData.season;
    const gameSeason = parseInt(gameSeasonStr, 10);

    if (gameSeason && gameSeason < currentSeason) {
        const mlbButtonsArray = mlbButtons._buttonArray;
        for (let i = mlbButtonsArray.length; i--;) {
            if (mlbButtonsArray[i].text === 'MLB.TV') {
                mlbButtonsArray.splice(i, 1);
            }
        }
    }

}

function gameSelection(data, opts = {}) {
    const { mlbMoment } = opts;
    const moment = mlbMoment.getMomentTimezone();

    if (!flipDate && typeof document !== 'undefined') {
        flipDate = require('flipDate/flipDate');
    }

    const gameDates = data.dates || [];
    let idx = gameDates.length - 1;
    const today = flipDate.today();

    if (gameDates && gameDates.length > 1) {
        // Iteration will stop before the last item in the array, because
        // the last item will be the default if an earlier date is not suitable.

        for (let i = 0; i < gameDates.length - 1; ++i) {
            // string comparison
            if ((gameDates[i].date || '').replace(/-/g, '/') >= today) {
                idx = i;
                break;
            }
        }
    } else if (idx === -1) {
        return null;
    }

    const games = get(gameDates, `[${idx}].games`, []);
    let gIndx = 0;

    games.forEach((game, i) => {
        const dateFormatted = moment(game.gameDate).format('YYYY/MM/DD');
        // string comparison
        if (dateFormatted >= today) {
            gIndx = i;
            return;
        }
    });

    return get(gameDates, `[${idx}].games[${gIndx}]`, {});

}

/**
 *
 * @function extender
 * @param {schedule_data_type|single_game_type} data
 * @param {Object} opts shared options
 * @returns {Object} extended data
 *
 */
function extender(data = {}, opts = {}) {
    let gameData = get(data, 'dates[0].games[0]', data);
    try {
        gameData = gameSelection(data, opts) || gameData;
    } catch (error) {
        console.error(
            'error in data extender: earliestNonPastDate:' + error);
    }

    const { gameUtils, mode = 'responsive', sportId = 1, teamId, mlbLinker, lang, isPostseasonPush, milbSportIds = '', youtubeMLBLink = '' } = opts;
    const { device = 'desktop', siteAssetsUri = SITE_ASSET_URI, siteImageUri = SITE_IMAGE_URI, defaultPromo } = opts;

    gameData.sport = getSport(sportId, milbSportIds);

    const extStatus = gameUtils.getExtendedStatus(gameData);
    const status = extendStatus(gameData, opts);
    const flag = getFlag(opts, gameData);
    const component = 'g5-component--mlb-scores';
    const { description = '' } = gameData;
    const players = getPlayers(opts, gameData);
    const challengingTeamAbbrv = getChallengingTeam(opts, gameData);

    const hideProbablePitchers = players
        && (players[0] && players[0].isTBD && players[0].typeCode === 'probable')
        && (players[1] && players[1].isTBD && players[1].typeCode === 'probable');

    let gameClasses = '';
    let gameTime = '';
    let gameTimePostseasonPush = '';
    let extendedLinescore = {};
    let note = {};
    let sponsoredBy = null;
    let extendedTeams = {};

    try {
        gameClasses = getGameClasses(component, mode, status, flag);
    } catch (e) {
        console.error('error in data extender: getGameClasses: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
    }

    try {
        gameTime = getGameTime(opts, gameData, status);
    } catch (e) {
        console.error('error in data extender: getGameTime: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
    }

    try {
        gameTimePostseasonPush = getGameTimePostseasonPush(opts, gameData, status);

    } catch (e) {
        console.error('error in data extender: getGameTimePostseasonPush: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
    }

    try {
        extendedLinescore = extendLinescore(gameData, status.abstract);
    } catch (e) {
        console.error('error in data extender: extendLinescore: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
    }

    try {
        note = getGameNote(gameData, mode, status, component);
    } catch (e) {
        console.error('error in data extender: getGameNote: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
    }

    try {
        sponsoredBy = getGameSponsor(opts, mode, gameData);
    } catch (e) {
        console.error('error in data extender: getGameSponsor: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
    }

    try {
        extendedTeams = extendTeams(opts, gameData.teams, status.abstract.isFinal);
    } catch (e) {
        console.error('error in data extender: extendTeams: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
    }

    const seriesStatusResult = (gameData.seriesStatus && gameData.seriesStatus.result) ? gameData.seriesStatus.result : '';

    let game = {
        gamePk: gameData.gamePk,
        css: gameClasses,
        wrapperClass: getGameWrapperClass(component, flag),
        gameTime,
        gameTimePostseasonPush,
        gamedayLink: mlbLinker && mlbLinker.getGamedayLink(gameData, true),
        linescore: extendedLinescore,
        note,
        players,
        gameType: getGameTypeDesc(gameData.gameType),
        hideProbablePitchers: hideProbablePitchers,
        seriesStatusResult: seriesStatusResult,
        description,
        status,
        teams: extendedTeams,
        track: getGameTracking(gameData),
        lang,
        challengingTeam: challengingTeamAbbrv,
        sponsoredBy,
        psScoresFullCoverage: i18n('ps_scores_full_coverage', 'Full Postseason Coverage'),
        isPostSeasonReady: extStatus.isPostSeasonReady,
        gameNumber: gameData.gameNumber
    };

    if (MODE_HAS_BUTTONS[mode]) {

        try {

            if (youtubeMLBLink) {
                gameData.youtubeMLBLink = youtubeMLBLink;
            }

            let mlbButtons = gameUtils.getButtons(gameData);

            if (mode === 'responsive') {

                hideMLBTV(gameData, mlbButtons, opts);

            }

            game.buttons = extendMlbButtons(mode, status.abstract, mlbButtons, flag, device);

            if (mode === 'minisb') {

                game.hasMiniButtonsSm = hasMiniButtonsSm(game.buttons, flag, device);
                game.isPostseasonPush = isPostseasonPush;
            }

        } catch (e) {
            console.error('error in data extender: getButtons: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
        }

    }

    //TODO: Clean up these mode checks
    if (mode !== 'mediawall') {

        game.flag = flag;

        // Used in minisb-hover-buttons.html to show live lookin on hover
        game.hasLiveLook = (device === 'desktop') && flag && (flag.type === 'live-lookin');
    }


    if (mode === 'responsive' || mode === 'MIG' || (mode === 'minisb' && game.isPostSeasonReady)) {

        let broadcasts = {};

        try {
            broadcasts = getBroadcasts(gameData.broadcasts, gameData, teamId);
        } catch (e) {
            console.error('error in data extender: getBroadcasts: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
        }

        game.broadcasts = broadcasts;

    }

    if (mode === 'responsive' || mode === 'MIG') {

        let venue = '';

        try {

            if (mode === 'responsive') {

                venue = i18n('at_venue', 'At {{venueName}}').replace('{{venueName}}', gameData.venue.name);

            } else {

                venue = gameData.venue.name;

            }

        } catch (e) {
            console.error('error in data extender: getVenue: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
        }

        game.venue = venue;

        game.interruptedText = gameUtils.getInterruptedGameStrs(gameData);

    }

    if (mode === 'responsive') {

        try {
            game.videoId = opts.videoEmbedder ? getVideoId(gameData) : null;
        } catch (e) {
            console.error('error in data extender: getVideoId: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
        }

        try {
            game.teamLeaders = getTeamLeaders(opts, gameData.teams, gameData.sport);
        } catch (e) {
            console.error('error in data extender: getTeamLeaders: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
        }

        try {
            game.homeRuns = getHomeRuns(opts, gameData);
        } catch (e) {
            console.error('error in data extender: getHomeRuns: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
        }

        game.description = getDescription(gameData);

    } else if (mode === 'MIG') {

        try {
            game.date = getDate(opts, gameData);
        } catch (e) {
            console.error('error in data extender: getDate: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
        }

        try {
            game.promotion = getPromotion(opts, gameData);
            if (game.promotion && gameData.sport !== 'milb') {
                game.hideProbablePitchers = true;
            }
        } catch (e) {
            console.error('error in data extender: getPromo: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
        }

        if (gameData.sport === 'milb' && game.isPostSeasonReady) {
            try {
                game.postseasonInfo = getPostseasonInfo(gameData);
            } catch (e) {
                console.error('error in data extender: getPostseasonInfo: (gamePk: ' + gameData.gamePk + '): ' + e.toString());
            }
        }
    }

    return {
        componentId: `${component}--${mode}--${gameData.gamePk}`,
        buttonOptions: getButtonOptions(opts),
        component,
        defaultPromo,
        version,
        HEADSHOT_SILHOUETTE,
        mode: getMode(mode),
        isMiLB: gameData.sport === 'milb',
        siteAssetsUri: siteAssetsUri,
        siteImageUri: siteImageUri,
        game
    };
}

export default extender;
